import { useState, useEffect, useCallback } from 'react';
import { fetchStatistics, type ReportViewer } from '@/lib/api';
import type { Statistics } from '@/types/statistics';

interface UseStatisticsResult {
  data: Statistics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Single data-fetching hook for the entire app.
 * Fetches + Zod-validates the statistics JSON once; `refetch` reloads it (after
 * the viewer corrects who is who, their own report). Refetching keeps the
 * current data on screen until the new one arrives.
 */
export function useStatistics(
  sessionId: string,
  viewer?: ReportViewer,
): UseStatisticsResult {
  const [data, setData] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const playerId = viewer?.playerId;
  const reportToken = viewer?.reportToken;

  const load = useCallback(async () => {
    setError(null);
    try {
      const stats = await fetchStatistics(
        sessionId,
        playerId ? { playerId, reportToken } : undefined,
      );
      setData(stats);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [sessionId, playerId, reportToken]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refetch: load };
}
